import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Search, ArrowUpDown, ArrowRight } from 'lucide-react';
import { useSteamUser } from '../hooks/useSteamUser';
import { GameAchievementsCard } from '../components/GameAchievements';
import { UserProfileHeader } from '../components/UserProfileHeader';

type SortOption = 'percentage' | 'recent' | 'alphabetical';

export function UserAchievementsPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, gameStats, isLoading, error } = useSteamUser(username || '');
  const [sortBy, setSortBy] = useState<SortOption>('percentage');
  const [searchQuery, setSearchQuery] = useState('');

  const sortedGames = useMemo(() => {
    if (!gameStats?.achievements) return [];

    const filteredGames = gameStats.achievements.filter(game =>
      game.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return [...filteredGames].sort((a, b) => {
      switch (sortBy) {
        case 'percentage':
          const percentageA = (a.unlockedAchievements / a.totalAchievements) * 100;
          const percentageB = (b.unlockedAchievements / b.totalAchievements) * 100;
          return percentageB - percentageA;
        case 'recent':
          const latestA = Math.max(...a.achievements.map(ach => ach.unlocktime));
          const latestB = Math.max(...b.achievements.map(ach => ach.unlocktime));
          return latestB - latestA;
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
  }, [gameStats?.achievements, sortBy, searchQuery]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user || !gameStats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Failed to load achievements'}</p>
          <button
            onClick={() => navigate(`/user/${username}`)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(`/user/${username}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Profile
        </button>

        <UserProfileHeader user={user} gameStats={gameStats} />

        <div className="mt-6 bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Achievement Progress
              </h1>
              <p className="text-gray-600">
                Tracking achievements across {gameStats.achievements.length} games
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search games..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="percentage">Highest Completion</option>
                  <option value="recent">Recently Played</option>
                  <option value="alphabetical">Alphabetical</option>
                </select>
                <ArrowUpDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedGames.map(game => (
              <div
                key={game.appid}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <GameAchievementsCard game={game} />
                <div className="px-4 pb-4">
                  <div className="mt-2 space-y-2">
                    {game.achievements.slice(0, 3).map(achievement => (
                      <div
                        key={achievement.apiname}
                        className="flex items-center gap-3 text-sm"
                      >
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="text-gray-900">{achievement.name}</span>
                      </div>
                    ))}
                    {game.achievements.length > 3 && (
                      <Link
                        to={`/user/${username}/game/${game.appid}/achievements`}
                        className="text-sm text-indigo-600 hover:text-indigo-700 pl-7 flex items-center gap-1 group"
                      >
                        +{game.achievements.length - 3} more achievements
                        <ArrowRight className="w-4 h-4 transform translate-x-0 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {sortedGames.length === 0 && (
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchQuery
                  ? 'No games found matching your search'
                  : 'No achievements unlocked yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}